import { useState, useMemo } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  ClipboardCheck,
  AlertTriangle,
  Clock,
  User,
  Send,
  Loader2,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ArrowRightLeft,
  Forward,
  XCircle,
  ListChecks,
  CheckCircle2,
  Users,
  RotateCcw,
} from "lucide-react";
import {
  V2_ALLOWED_TRANSITIONS,
  V2_STATUS_LABELS,
  type V2ActionItemStatus,
} from "@shared/v2/constants";
import type {
  ActionItemsListResponse,
  ActionItemRecord,
  LanesResponse,
  ReferenceListsResponse,
  TransitionPermissionRecord,
} from "@shared/v2/types";
import { useAuth } from "@/hooks/use-auth";
import { HandoffReasonModal, type HandoffReasonResult } from "../components/HandoffReasonModal";

const CLOSED_STATUSES = ["CLOSED", "CANCELLED", "SENT_UPWARD"];

interface PostponeDialogState {
  itemId: string;
  title: string;
  currentDueDate: string;
  postponeCount: number;
}

function PostponeDialog({
  state,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  state: PostponeDialogState;
  onClose: () => void;
  onSubmit: (payload: { newDueDate: string; reasonCategory: string; reasonNote?: string }) => void;
  isSubmitting: boolean;
}) {
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const remaining = POSTPONE_CAP - state.postponeCount;
  const noteRequired = reason === "OTHER";
  const dateValid = !!newDate && newDate > state.currentDueDate;
  const canSubmit = dateValid && !!reason && (!noteRequired || note.trim().length > 0);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#1a1f2e] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[#00CFE8]" />
            Postpone Task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-white/80 font-medium" data-testid="text-postpone-title">{state.title}</p>
          <div className="text-white/50 text-xs space-y-0.5">
            <p>Current due date: <span className="text-white/70">{formatDate(state.currentDueDate)}</span></p>
            <p>
              Postpones used: <span className={state.postponeCount >= POSTPONE_CAP ? "text-red-400" : "text-white/70"}>{state.postponeCount}/{POSTPONE_CAP}</span>
              {" · "}
              Remaining after this: <span className="text-white/70">{Math.max(0, remaining - 1)}</span>
            </p>
            {remaining <= 1 && (
              <p className="text-[#FF9F43] text-sm mt-1">
                {remaining === 1
                  ? "Last allowed postpone. Further extension will require escalating to SDF PMO via Reassign."
                  : "Postpone cap reached — this request will be rejected."}
              </p>
            )}
          </div>
          <div>
            <label className="block text-white/60 text-xs mb-1">New due date</label>
            <input
              type="date"
              value={newDate}
              min={state.currentDueDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="bg-white/5 border border-white/10 rounded px-2 h-9 w-full text-white text-sm [color-scheme:dark]"
              data-testid="input-postpone-date"
            />
            {newDate && !dateValid && (
              <p className="text-red-400 text-sm mt-1">New date must be later than the current due date.</p>
            )}
          </div>
          <div>
            <label className="block text-white/60 text-xs mb-1">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white h-9 text-sm" data-testid="select-postpone-reason">
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent>
                {POSTPONE_REASONS.map((r) => (
                  <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-white/60 text-xs mb-1">
              Note {noteRequired ? <span className="text-red-400">(required)</span> : <span className="text-white/30">(optional)</span>}
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={noteRequired ? "Explain why you need to postpone..." : "Optional context..."}
              className="bg-white/5 border-white/10 text-white text-sm resize-none placeholder:text-white/25"
              data-testid="input-postpone-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="border-white/10 text-white/60"
            data-testid="button-postpone-cancel"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit || isSubmitting}
            onClick={() => onSubmit({ newDueDate: newDate, reasonCategory: reason, reasonNote: note.trim() || undefined })}
            className="bg-[#00CFE8] hover:bg-[#00CFE8]/80 text-black gap-1.5"
            data-testid="button-postpone-submit"
          >
            {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />}
            Postpone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v2.10.x — Postpone reasons (kept in sync with POSTPONE_REASON_LABELS in
// server/v2/routes.ts). Used by the "Tasks I've delegated" section's
// Postpone modal. Hard cap = 2; the 3rd attempt is blocked server-side.
const POSTPONE_REASONS: { key: string; label: string }[] = [
  { key: "HOLDER_REQUESTED_TIME", label: "Holder requested more time" },
  { key: "AWAITING_DEPENDENCY", label: "Awaiting external dependency" },
  { key: "OWNER_REPRIORITISED", label: "Re-prioritised by owner" },
  { key: "OTHER", label: "Other (note required)" },
];
const POSTPONE_CAP = 2;

function isOverdue(dueDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + "T00:00:00");
  return due < today;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function daysUntilDue(dueDateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + "T00:00:00");
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function priorityColor(priority: string): string {
  switch (priority) {
    case "P1": return "text-red-400 border-red-400/30";
    case "P2": return "text-orange-400 border-orange-400/30";
    case "P3": return "text-blue-400 border-blue-400/30";
    case "P4": return "text-white/50 border-white/20";
    default: return "text-white/50 border-white/20";
  }
}

function statusLabel(status: string): string {
  return V2_STATUS_LABELS[status as V2ActionItemStatus] || status.replace(/_/g, " ");
}

function suggestResponsible(
  targetStatus: string,
  assignedTo: string,
  targets: string[],
): string {
  switch (targetStatus) {
    case "WITH_SDF_MD_REVIEW": {
      const m = targets.find(t => t.toLowerCase().includes("sdf") && t.toLowerCase().includes("md"));
      return m || "";
    }
    case "WITH_EDGE_MD_REVIEW": {
      const m = targets.find(t => t.toLowerCase().includes("edge") && t.toLowerCase().includes("md"));
      return m || "";
    }
    case "WITH_SECRETARIAT_TO_SEND": {
      const m = targets.find(t => t.toLowerCase().includes("secretariat") || t.toLowerCase().includes("pmo"));
      return m || "";
    }
    case "IN_PROGRESS":
      return assignedTo || "";
    default:
      return "";
  }
}

function formatDuration(sinceStr: string | null | undefined): string {
  if (!sinceStr) return "";
  const since = new Date(sinceStr);
  const now = new Date();
  const diffMs = now.getTime() - since.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

export default function V2UpdatesPage() {
  const { containerClass } = useLayout();
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { isAdmin: userIsAdmin, isSuperAdmin, isMember, teamMemberName, canConfirmDroppedTheBall } = useAuth();
  const { data: cSuiteResp } = useQuery<{ data: { name: string }[] }>({
    queryKey: ["/api/people/c-suite"],
    staleTime: 60_000,
  });
  const cSuiteNames = useMemo(() => (cSuiteResp?.data || []).map((p) => p.name), [cSuiteResp]);
  const [updateTexts, setUpdateTexts] = useState<Record<string, string>>({});
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const [inlineHandoffSelections, setInlineHandoffSelections] = useState<Record<string, string>>({});
  // Optional internal "due back by" date + reason the holder can attach to
  // an inline handoff. Mirrors the flow on the action-item-detail page so
  // a user can set the date without leaving the Updates page. Same
  // /handoff endpoint, same fields (internalDueDate / internalDueReason),
  // so reminder scheduler, KPI grading, Excel export and Employee Dossier
  // all light up automatically.
  const [inlineHandoffDates, setInlineHandoffDates] = useState<Record<string, string>>({});
  const [inlineHandoffReasons, setInlineHandoffReasons] = useState<Record<string, string>>({});
  const [pendingTransition, setPendingTransition] = useState<{ itemId: string; status: string; responsible?: string } | null>(null);

  // Note: the per-person workload pill that briefly lived in this page's
  // section headers was removed — it was redundant with the existing
  // owned/handed/overdue chips while the Updates view shows everything
  // unfiltered. The same data is still surfaced on the action item
  // detail page where it isn't redundant. If filters are added to this
  // page later, reintroducing the pill (scoped-vs-global distinction)
  // becomes useful again.

  const itemsQuery = useQuery<ActionItemsListResponse>({
    queryKey: ["/api/action-items"],
  });

  const lanesQuery = useQuery<LanesResponse>({
    queryKey: ["/api/governance/lanes"],
  });

  const refListsQuery = useQuery<ReferenceListsResponse>({
    queryKey: ["/api/governance/reference-lists"],
  });

  const lanes = lanesQuery.data?.data ?? [];
  const allItems = itemsQuery.data?.data ?? [];
  const peopleQuery = useQuery<{ team_members: { name: string; team_function: string | null }[]; authority_lanes: { lane_key: string; display_name: string }[]; combined: string[]; combined_meta?: { name: string; is_workflow_actor: boolean }[] }>({
    queryKey: ["/api/people"],
  });
  const combinedPeople = peopleQuery.data?.combined ?? [];
  const combinedMeta = peopleQuery.data?.combined_meta ?? combinedPeople.map(name => ({ name, is_workflow_actor: false }));
  const assignablePeople = combinedMeta.filter(m => !m.is_workflow_actor).map(m => m.name);
  const responsibilityTargets = assignablePeople.map((name, i) => ({ item_key: `person-${i}`, label: name }));
  const targetLabels = combinedPeople;

  const sdfMdTarget = useMemo(
    () => combinedPeople.find((n) => /sdf/i.test(n) && /\bmd\b/i.test(n) && !/pmo/i.test(n)),
    [combinedPeople]
  );
  const sdfPmoTarget = useMemo(
    () => combinedPeople.find((n) => /sdf/i.test(n) && /pmo/i.test(n)),
    [combinedPeople]
  );

  interface SubSummary { total: number; completed: number; pending: number; overdue: number; subs: { id: string; assigned_to: string; instruction: string; due_date: string; status: string; assigned_by: string }[] }
  const subSummariesQuery = useQuery<{ data: Record<string, SubSummary> }>({
    queryKey: ["/api/sub-assignments/summaries"],
    queryFn: async () => {
      const res = await fetch("/api/sub-assignments/summaries", { credentials: "include" });
      if (!res.ok) return { data: {} };
      return res.json();
    },
  });
  const subSummaries = subSummariesQuery.data?.data ?? {};

  const transPermsQuery = useQuery<{ data: TransitionPermissionRecord[] }>({
    queryKey: ["/api/governance/transition-permissions"],
  });
  const transPerms = transPermsQuery.data?.data ?? [];

  const getFilteredTransitions = (itemStatus: string) => {
    const all = V2_ALLOWED_TRANSITIONS[itemStatus as V2ActionItemStatus] || [];
    if (isSuperAdmin) return all;
    return all.filter((targetStatus) => {
      const rulesForTarget = transPerms.filter(
        (tp) => tp.to_status === targetStatus && tp.can_perform
      );
      if (rulesForTarget.length === 0) return true;
      return rulesForTarget.some((r) => r.person_name === teamMemberName);
    });
  };

  const openItems = useMemo(
    () => {
      // Trust the backend for scope. /api/action-items already restricts a
      // plain "member" role to their own + responsible + sub-assigned items,
      // and widens a department-head (any role with team_members.is_department_head=true
      // and a department_id) to include every item assigned to or currently
      // with anyone in their department. Re-narrowing here on `isMember`
      // silently undid the dept-head widening for member+flag users — see
      // server/v2/routes.ts GET /action-items for the canonical filter.
      return allItems.filter((item) => !CLOSED_STATUSES.includes(item.status));
    },
    [allItems]
  );

  const sortItems = (items: ActionItemRecord[]) => {
    return [...items].sort((a, b) => {
      const aOverdue = isOverdue(a.due_date) ? 0 : 1;
      const bOverdue = isOverdue(b.due_date) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      const priorityOrder = ["P1", "P2", "P3", "P4"];
      return priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority);
    });
  };

  // v2.10.x — Personal two-section model. Each open task renders ONCE in
  // the section that matches the current viewer's relationship to it:
  //   1. "My Action Items" — items I am currently holding (full
  //      interactive renderItemRow). If I am also the owner, that's fine
  //      — the row still belongs here because I can act on it.
  //   2. "Tasks I've Delegated" — items I OWN but someone else currently
  //      holds (compact read-only row with Reassign + Postpone only).
  // Cross-team visibility (the previous per-person grouping for dept
  // heads / admins) lives on the KPI Dashboard. This page is
  // intentionally personal so people see their own slice and aren't
  // overwhelmed scrolling past everyone else's work.
  //
  // Admin-only accounts (no teamMemberName) have no personal slice, so
  // we fall back to the old flat list of all open items in section 1.
  const { myHeldItems, myDelegatedItems, otherItems } = useMemo(() => {
    if (!teamMemberName) {
      return { myHeldItems: sortItems(openItems), myDelegatedItems: [] as ActionItemRecord[], otherItems: [] as ActionItemRecord[] };
    }
    const held: ActionItemRecord[] = [];
    const delegated: ActionItemRecord[] = [];
    const other: ActionItemRecord[] = [];
    for (const item of openItems) {
      const iAmHolder = item.current_responsible === teamMemberName ||
        (!item.current_responsible && item.assigned_to === teamMemberName);
      const iAmOwner = item.assigned_to === teamMemberName;
      if (iAmHolder) {
        held.push(item);
      } else if (iAmOwner) {
        delegated.push(item);
      } else {
        other.push(item);
      }
    }
    // Section 2 is sorted by due date overdue-first (existing sortItems
    // already prioritises overdue, then by P-band — exactly what we need).
    return {
      myHeldItems: sortItems(held),
      myDelegatedItems: sortItems(delegated),
      otherItems: sortItems(other),
    };
  }, [openItems, teamMemberName]);

  const totalOverdue = useMemo(
    () => openItems.filter((i) => isOverdue(i.due_date)).length,
    [openItems]
  );

  function getLaneDisplay(laneKey: string): string {
    const lane = lanes.find((l) => l.lane_key === laneKey);
    return lane?.display_name ?? laneKey;
  }

  const submitUpdate = useMutation({
    mutationFn: async ({ itemId, text, skipNotification }: { itemId: string; text: string; skipNotification?: boolean }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/events`, {
        eventType: "PROGRESS_UPDATE",
        payload: { note: text },
        skipNotification: skipNotification || false,
      });
      return res.json();
    },
  });

  const statusTransition = useMutation({
    mutationFn: async ({ itemId, status, responsible }: { itemId: string; status: string; responsible?: string }) => {
      const body: Record<string, string> = { status };
      if (responsible) body.currentResponsible = responsible;
      const res = await apiRequest("PATCH", `/api/action-items/${itemId}`, body);
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: "Status updated" });
    },
    onError: (err: Error) => {
      showFromApiError("Status Change Failed", err);
    },
  });

  const directHandoff = useMutation({
    mutationFn: async ({ itemId, handTo, note, reasonCategory, reasonNote, escalateTo, internalDueDate, internalDueReason }: { itemId: string; handTo: string; note?: string; reasonCategory?: string; reasonNote?: string; escalateTo?: string; internalDueDate?: string; internalDueReason?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/handoff`, {
        handTo,
        note: note || undefined,
        reasonCategory: reasonCategory || undefined,
        reasonNote: reasonNote || undefined,
        escalateTo: escalateTo || undefined,
        internalDueDate: internalDueDate || undefined,
        internalDueReason: internalDueReason || undefined,
      });
      return res.json();
    },
  });

  const [reasonModalState, setReasonModalState] = useState<{
    itemId: string;
    handTo: string;
    text: string;
    currentHolder: string;
    title: string;
  } | null>(null);

  // v2.10.x — Tasks I've Delegated section state.
  // - delegatedReassignSelections: per-item picked target for the
  //   inline "Reassign to..." flow. Presence of the key (even with an
  //   empty value) means the picker is open. On Continue we fold into
  //   the existing reasonModalState pipeline so the same reason-capture
  //   modal serves both the Updates page handoff and the delegated
  //   reassign — one source of truth.
  // - postponeModalState: drives the Postpone dialog. Owner-only;
  //   server enforces the cap of 2.
  const [delegatedReassignSelections, setDelegatedReassignSelections] = useState<Record<string, string>>({});
  const [postponeModalState, setPostponeModalState] = useState<{
    itemId: string;
    title: string;
    currentDueDate: string;
    postponeCount: number;
  } | null>(null);

  const postponeMutation = useMutation({
    mutationFn: async ({ itemId, newDueDate, reasonCategory, reasonNote }: { itemId: string; newDueDate: string; reasonCategory: string; reasonNote?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/postpone`, {
        newDueDate,
        reasonCategory,
        reasonNote: reasonNote || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      setPostponeModalState(null);
      toast({ title: "Task postponed" });
    },
    onError: (err: Error) => {
      showFromApiError("Postpone Failed", err);
    },
  });

  function handleDelegatedReassignContinue(item: ActionItemRecord) {
    const target = (delegatedReassignSelections[item.id] || "").trim();
    if (!target) return;
    if (target === item.current_responsible) {
      toast({ title: "Already with this person", description: "Pick a different target.", variant: "destructive" });
      return;
    }
    // Hand off to the shared reason-capture flow used by the Updates
    // page's standard reassign — keeps audit, escalation routing, and
    // categorical reason persistence consistent.
    setReasonModalState({
      itemId: item.id,
      handTo: target,
      text: "",
      currentHolder: item.current_responsible || item.assigned_to,
      title: item.title,
    });
    setDelegatedReassignSelections(prev => { const n = { ...prev }; delete n[item.id]; return n; });
  }

  function renderDelegatedRow(item: ActionItemRecord) {
    const overdue = isOverdue(item.due_date);
    const days = daysUntilDue(item.due_date);
    const postponeCount = item.postpone_count ?? 0;
    const atCap = postponeCount >= POSTPONE_CAP;
    const reassignTarget = delegatedReassignSelections[item.id];
    const showReassignSelect = reassignTarget !== undefined;

    return (
      <div
        key={item.id}
        className={`rounded-md p-3 ${overdue ? "bg-red-500/8 border border-red-500/30" : "bg-white/4 border border-white/8"}`}
        data-testid={`delegated-row-${item.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
          <div className="flex-1 min-w-0">
            {item.meeting_name && (
              <p className="text-white/50 text-sm truncate mb-0.5">{item.meeting_name}</p>
            )}
            <a
              href={`/action-items/${item.id}`}
              className="text-white font-medium text-sm hover:text-[#00CFE8] transition-colors"
              data-testid={`text-delegated-title-${item.id}`}
            >
              {item.title}
            </a>
            <p className="text-white/40 text-xs">{getLaneDisplay(item.authority_lane)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge variant="outline" className={priorityColor(item.priority)}>{item.priority}</Badge>
            <Badge variant="outline" className="text-white/60 border-white/15 text-xs">{statusLabel(item.status)}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap mb-2">
          <span
            className={`flex items-center gap-1 ${overdue ? "text-red-400 font-medium" : "text-white/50"}`}
            data-testid={`text-delegated-due-${item.id}`}
          >
            {overdue ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
            Due {formatDate(item.due_date)}
            {overdue
              ? ` (${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue)`
              : days === 0
                ? " (today)"
                : days <= 7
                  ? ` (${days} day${days !== 1 ? "s" : ""} left)`
                  : ""}
          </span>
          {item.current_responsible && (
            <span className="flex items-center gap-1 text-[#FF9F43]/80" data-testid={`text-delegated-holder-${item.id}`}>
              <User className="h-3 w-3" />
              With: {item.current_responsible}
              {item.responsible_since && (
                <span className="text-white/30 ml-1">({formatDuration(item.responsible_since)})</span>
              )}
            </span>
          )}
          {postponeCount > 0 && (
            <span
              className={`flex items-center gap-1 ${atCap ? "text-red-400" : "text-white/40"}`}
              title={atCap ? "Postpone cap reached. Use Reassign → SDF PMO to extend further." : ""}
              data-testid={`text-postpone-count-${item.id}`}
            >
              <Clock className="h-3 w-3" />
              Postponed {postponeCount}/{POSTPONE_CAP}
            </span>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap pt-2 border-t border-white/8">
          {!showReassignSelect ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-[#FF9F43] border-[#FF9F43]/30 hover:bg-[#FF9F43]/10 hover:text-[#FF9F43] h-7 text-xs"
                onClick={() => setDelegatedReassignSelections(prev => ({ ...prev, [item.id]: "" }))}
                data-testid={`button-delegated-reassign-${item.id}`}
              >
                <Forward className="h-3 w-3" /> Reassign
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={atCap}
                className="gap-1.5 text-[#00CFE8] border-[#00CFE8]/30 hover:bg-[#00CFE8]/10 hover:text-[#00CFE8] h-7 text-xs disabled:opacity-50"
                title={atCap
                  ? `Postpone cap reached (${POSTPONE_CAP}/${POSTPONE_CAP}). To extend further, use Reassign and route to SDF PMO.`
                  : `Postpone task (${postponeCount}/${POSTPONE_CAP} used)`}
                onClick={() => setPostponeModalState({
                  itemId: item.id,
                  title: item.title,
                  currentDueDate: item.due_date,
                  postponeCount,
                })}
                data-testid={`button-delegated-postpone-${item.id}`}
              >
                <CalendarClock className="h-3 w-3" />
                {atCap ? "Postpone (capped)" : "Postpone"}
              </Button>
            </>
          ) : (
            <>
              <Select
                value={reassignTarget || ""}
                onValueChange={v => setDelegatedReassignSelections(prev => ({ ...prev, [item.id]: v }))}
              >
                <SelectTrigger
                  className="w-44 bg-white/5 border-white/10 text-white text-xs h-7"
                  data-testid={`select-delegated-reassign-${item.id}`}
                >
                  <SelectValue placeholder="Reassign to..." />
                </SelectTrigger>
                <SelectContent>
                  {responsibilityTargets.map(t => (
                    <SelectItem key={t.item_key} value={t.label}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDelegatedReassignSelections(prev => { const n = { ...prev }; delete n[item.id]; return n; })}
                className="h-7 text-white/40 text-xs"
                data-testid={`button-delegated-reassign-cancel-${item.id}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!reassignTarget}
                onClick={() => handleDelegatedReassignContinue(item)}
                className="h-7 text-xs bg-[#FF9F43] hover:bg-[#FF9F43]/80 text-black gap-1.5"
                data-testid={`button-delegated-reassign-continue-${item.id}`}
              >
                Continue
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  const sendForReview = useMutation({
    mutationFn: async ({ itemId, handTo, note }: { itemId: string; handTo: string; note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/handoff`, {
        handTo,
        note: note || undefined,
        transitionTo: "WITH_SDF_MD_REVIEW",
      });
      return res.json();
    },
  });

  function handleSendForReview(itemId: string, target: string) {
    const text = (updateTexts[itemId] ?? "").trim();
    setSubmittingIds((prev) => new Set(prev).add(itemId));
    sendForReview.mutate(
      { itemId, handTo: target, note: text || undefined },
      {
        onSuccess: () => {
          setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
          setUpdateTexts((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
          setInlineHandoffSelections((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
          setInlineHandoffDates((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
          setInlineHandoffReasons((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
          queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
          toast({ title: `Sent to ${target} for review` });
        },
        onError: (err: Error) => {
          setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
          showFromApiError("Send for Review Failed", err);
        },
      }
    );
  }

  const TERMINAL_STATUSES = ["CLOSED", "CANCELLED", "SENT_UPWARD"];

  function handleStatusSelect(itemId: string, status: string, assignedTo: string) {
    const suggested = suggestResponsible(status, assignedTo, targetLabels);
    const payload = { itemId, status, responsible: suggested || undefined };

    if (TERMINAL_STATUSES.includes(status)) {
      setPendingTransition(payload);
    } else {
      statusTransition.mutate(payload);
    }
  }

  // handToOverride lets a caller (e.g. the "Return to owner" button)
  // supply the target directly instead of relying on
  // inlineHandoffSelections state. React state setters are async, so a
  // call site that does `setInlineHandoffSelections(...)` immediately
  // followed by `handleSubmitUpdate(...)` would otherwise read the STALE
  // selection (undefined for that item) on the same tick and silently
  // bail at the `if (!text && !handTo) return;` guard below — no API
  // call, no error, the user thinks they clicked but nothing happens.
  function handleSubmitUpdate(itemId: string, handToOverride?: string) {
    const text = (updateTexts[itemId] ?? "").trim();
    let handTo: string | undefined = handToOverride ?? inlineHandoffSelections[itemId];
    if (!text && !handTo) return;

    const item = openItems.find(i => i.id === itemId);
    if (handTo && item && handTo === item.current_responsible) {
      handTo = undefined;
    }

    if (
      handTo &&
      item &&
      item.current_responsible &&
      item.current_responsible !== handTo &&
      (!teamMemberName || item.current_responsible !== teamMemberName)
    ) {
      setReasonModalState({
        itemId,
        handTo,
        text,
        currentHolder: item.current_responsible,
        title: item.title,
      });
      return;
    }

    setSubmittingIds((prev) => new Set(prev).add(itemId));

    const internalDueDate = (inlineHandoffDates[itemId] || "").trim() || undefined;
    // Reason is only meaningful in the context of a date — if the user
    // typed a reason then cleared the date, drop the orphaned reason
    // before submit so we never persist `current_internal_due_reason`
    // with a null `current_internal_due_date`.
    const internalDueReason = internalDueDate
      ? ((inlineHandoffReasons[itemId] || "").trim() || undefined)
      : undefined;

    const cleanup = () => {
      setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      setUpdateTexts((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffSelections((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffDates((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffReasons((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
    };

    const stopSubmitting = () => {
      setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    };

    if (text && handTo) {
      submitUpdate.mutate({ itemId, text, skipNotification: true }, {
        onSuccess: () => {
          directHandoff.mutate({ itemId, handTo: handTo!, note: text, internalDueDate, internalDueReason }, {
            onSuccess: () => { cleanup(); toast({ title: "Update recorded and item handed off" }); },
            onError: () => { cleanup(); showFromApiError("Handoff Failed", { message: "Update was saved, but the handoff could not be completed." }); },
          });
        },
        onError: (err: Error) => {
          stopSubmitting();
          showFromApiError("Update Failed", err);
        },
      });
    } else if (text) {
      submitUpdate.mutate({ itemId, text }, {
        onSuccess: () => { cleanup(); toast({ title: "Progress update recorded" }); },
        onError: (err: Error) => {
          stopSubmitting();
          showFromApiError("Update Failed", err);
        },
      });
    } else if (handTo) {
      directHandoff.mutate({ itemId, handTo, internalDueDate, internalDueReason }, {
        onSuccess: () => { cleanup(); toast({ title: "Item handed off successfully" }); },
        onError: (err: Error) => {
          stopSubmitting();
          showFromApiError("Handoff Failed", err);
        },
      });
    } else {
      stopSubmitting();
    }
  }

  function handleConfirmReason(result: HandoffReasonResult) {
    if (!reasonModalState) return;
    const { itemId, handTo, text } = reasonModalState;
    setSubmittingIds((prev) => new Set(prev).add(itemId));

    const internalDueDate = (inlineHandoffDates[itemId] || "").trim() || undefined;
    const internalDueReason = internalDueDate
      ? ((inlineHandoffReasons[itemId] || "").trim() || undefined)
      : undefined;

    const cleanup = () => {
      setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
      setUpdateTexts((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffSelections((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffDates((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      setInlineHandoffReasons((prev) => { const n = { ...prev }; delete n[itemId]; return n; });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      setReasonModalState(null);
    };
    const stopSubmitting = () => {
      setSubmittingIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; });
    };

    const fireHandoff = () => {
      directHandoff.mutate(
        {
          itemId,
          handTo,
          note: text || undefined,
          reasonCategory: result.reasonCategory,
          reasonNote: result.reasonNote,
          internalDueDate,
          internalDueReason,
        },
        {
          onSuccess: () => { cleanup(); toast({ title: "Item reassigned" }); },
          onError: (err: Error) => {
            stopSubmitting();
            showFromApiError("Reassignment Failed", err);
          },
        }
      );
    };

    if (text) {
      submitUpdate.mutate({ itemId, text, skipNotification: true }, {
        onSuccess: () => fireHandoff(),
        onError: () => {
          stopSubmitting();
          showFromApiError("Update Failed", { message: "Update could not be saved." });
        },
      });
    } else {
      fireHandoff();
    }
  }

  interface MySubAssignment {
    id: string;
    action_item_id: string;
    assigned_to: string;
    instruction: string;
    due_date: string;
    status: string;
    assigned_by: string;
    created_at: string;
    action_item: { id: string; title: string; authority_lane: string; priority: string; status: string };
  }

  const mySubsQuery = useQuery<{ data: MySubAssignment[] }>({
    queryKey: ["/api/my-sub-assignments"],
    queryFn: async () => {
      const res = await fetch("/api/my-sub-assignments", { credentials: "include" });
      if (!res.ok) return { data: [] };
      return res.json();
    },
  });

  const mySubs = mySubsQuery.data?.data ?? [];
  const [subCompletionNotes, setSubCompletionNotes] = useState<Record<string, string>>({});

  const completeSubMutation = useMutation({
    mutationFn: async ({ subId, note }: { subId: string; note?: string }) => {
      const res = await apiRequest("POST", `/api/sub-assignments/${subId}/complete`, { note });
      return res.json();
    },
    onSuccess: (_data, variables) => {
      toast({ title: "Sub-task completed and handed back" });
      setSubCompletionNotes(prev => { const n = { ...prev }; delete n[variables.subId]; return n; });
      queryClient.invalidateQueries({ queryKey: ["/api/my-sub-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sub-assignments/summaries"] });
    },
    onError: (err: any) => {
      showFromApiError("Error", err);
    },
  });

  const isLoading = itemsQuery.isLoading || lanesQuery.isLoading;
  const hasError = itemsQuery.isError || lanesQuery.isError;

  function renderItemRow(item: ActionItemRecord, showOwner = false, inMyCard = false) {
    const overdue = isOverdue(item.due_date);
    const days = daysUntilDue(item.due_date);
    const updateText = updateTexts[item.id] ?? "";
    const isSubmitting = submittingIds.has(item.id);
    // Strict authority model (v2.10.x): a non-admin can act on a task only
    // from cards relating to themselves. From their OWN card they may:
    //   - update progress / transition / send-for-review IF they currently
    //     hold the item (iAmHolder),
    //   - reassign-with-reason IF they own the item (the "Hand to" + reason
    //     modal flow), even when someone else holds it.
    // From any OTHER person's card, all controls are disabled — they must
    // go to their own section to act, or to the Escalations page to ask
    // a senior reviewer to step in.
    const iAmHolder = !!teamMemberName && item.current_responsible === teamMemberName;
    const iAmOwner = !!teamMemberName && item.assigned_to === teamMemberName;
    // Strict authority model (v2.10.x — no overrides). Only the current
    // HOLDER may act on a task in flight (progress, transition, send for
    // review). The OWNER may reassign-with-reason from their own card to
    // take the task back from a non-responsive recipient — that is the
    // ONLY legitimate path for a non-holder to intervene. NO admin
    // bypass, NO super_admin override. If you need to act on someone
    // else's task, reassign it to yourself first.
    const canActAsHolder = iAmHolder;
    const canReassignFromOwnSection = inMyCard && iAmOwner;
    const canDoAnything = canActAsHolder || canReassignFromOwnSection;
    const lockedReason = canDoAnything
      ? ""
      : !inMyCard
        ? "Only the current holder can act on this task. Open your own section if you own it and reassign it to yourself first."
        : "This task is no longer with you. Reassign it back to yourself with a reason to act on it again.";

    return (
      <div
        key={item.id}
        className={`rounded-md p-4 ${
          overdue
            ? "bg-red-500/8 border border-red-500/20"
            : "bg-white/4 border border-white/8"
        }`}
        data-testid={`item-row-${item.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="flex-1 min-w-0">
            {item.meeting_name && (
              <p
                className="text-white/70 text-xs truncate mb-0.5"
                data-testid={`text-item-opportunity-${item.id}`}
              >
                {item.meeting_name}
              </p>
            )}
            <p
              className="text-white font-medium text-sm truncate"
              data-testid={`text-item-title-${item.id}`}
            >
              {item.title}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-white/40 text-xs truncate">
                {getLaneDisplay(item.authority_lane)}
              </p>
              {showOwner && (
                <span className="text-white/30 text-xs">
                  (Owner: {item.assigned_to})
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge
              variant="outline"
              className={priorityColor(item.priority)}
            >
              {item.priority}
            </Badge>
            <Badge variant="outline" className="text-white/60 border-white/15">
              {statusLabel(item.status)}
            </Badge>
          </div>
        </div>

        {/* Phase 2 of Handoff Deadlines: when the holder has been given
            an internal "due back by" date AND it differs from the official
            due date AND I'm the holder, surface BOTH dates and drive the
            countdown / red colour off the internal date — that's the
            commitment that matters to me. Other viewers still see the
            board-facing due date as primary. */}
        {(() => {
          const hasInternal =
            !!item.current_internal_due_date &&
            item.current_internal_due_date !== item.due_date &&
            iAmHolder;
          const internalDate = hasInternal ? item.current_internal_due_date! : null;
          const internalOverdue = internalDate ? isOverdue(internalDate) : false;
          const internalDays = internalDate ? daysUntilDue(internalDate) : 0;
          const primaryOverdue = internalDate ? internalOverdue : overdue;
          const primaryDays = internalDate ? internalDays : days;
          return (
            <div className="flex items-center gap-4 mb-3 text-xs flex-wrap">
              <span
                className={`flex items-center gap-1 ${
                  primaryOverdue ? "text-red-400 font-medium" : "text-white/50"
                }`}
                data-testid={`text-due-${item.id}`}
              >
                {primaryOverdue ? (
                  <AlertTriangle className="h-3 w-3" />
                ) : (
                  <CalendarClock className="h-3 w-3" />
                )}
                {internalDate ? "Internal " : "Due "}
                {formatDate(internalDate ?? item.due_date)}
                {primaryOverdue
                  ? ` (${Math.abs(primaryDays)} day${Math.abs(primaryDays) !== 1 ? "s" : ""} overdue)`
                  : primaryDays === 0
                    ? " (today)"
                    : primaryDays <= 7
                      ? ` (${primaryDays} day${primaryDays !== 1 ? "s" : ""} left)`
                      : ""}
              </span>
              {internalDate && (
                <span
                  className="text-white/35"
                  data-testid={`text-board-due-${item.id}`}
                  title={
                    item.current_internal_due_reason
                      ? `Internal deadline reason: ${item.current_internal_due_reason}`
                      : undefined
                  }
                >
                  Board {formatDate(item.due_date)}
                </span>
              )}
              {item.doc_type && (
                <span className="text-white/40">{item.doc_type}</span>
              )}
            </div>
          );
        })()}

        {!showOwner && (subSummaries[item.id]?.subs.filter(s => s.status !== "COMPLETED").length > 0 ? (
          <div className="flex items-center gap-1.5 text-xs mb-1">
            <Users className="h-3 w-3 text-[#2563eb]" />
            <span className="text-[#2563eb]/80">
              With: {[...new Set(subSummaries[item.id].subs.filter(s => s.status !== "COMPLETED").map(s => s.assigned_to))].join(", ")}
            </span>
            <span className="text-white/30">(sub-tasks)</span>
          </div>
        ) : item.current_responsible ? (
          <div className="flex items-center gap-1.5 text-xs mb-1">
            <User className="h-3 w-3 text-[#FF9F43]" />
            <span className="text-[#FF9F43]/80">
              With: {item.current_responsible}
            </span>
            {item.responsible_since && (
              <span className="text-white/30">
                ({formatDuration(item.responsible_since)})
              </span>
            )}
          </div>
        ) : null)}

        {item.instruction && (
          <div className="mb-3 p-2.5 rounded bg-white/4 border border-white/8" data-testid={`text-instruction-${item.id}`}>
            <p className="text-xs font-semibold text-white/30 uppercase tracking-wider mb-1">Instruction</p>
            <p className="text-white/70 text-xs leading-relaxed whitespace-pre-wrap">{item.instruction}</p>
          </div>
        )}

        {item.last_return_note && (
          <div className="mb-3 p-2.5 rounded bg-[#FF9F43]/8 border border-[#FF9F43]/25" data-testid={`text-return-note-${item.id}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <RotateCcw className="h-3 w-3 text-[#FF9F43]" />
              <p className="text-xs font-semibold text-[#FF9F43] uppercase tracking-wider">Returned for Amendments</p>
              {item.last_return_by && (
                <span className="text-xs text-white/30 ml-1">by {item.last_return_by}</span>
              )}
              {item.last_return_date && (
                <span className="text-xs text-white/20 ml-1">
                  {new Date(item.last_return_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </span>
              )}
            </div>
            <p className="text-[#FF9F43]/90 text-xs leading-relaxed whitespace-pre-wrap">{item.last_return_note}</p>
          </div>
        )}

        {subSummaries[item.id] && (
          <div className="mb-3 p-2.5 rounded border border-[#2563eb]/20 bg-[#2563eb]/5" data-testid={`sub-tasks-summary-${item.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <ListChecks className="h-3.5 w-3.5 text-[#2563eb]" />
                <span className="text-xs font-semibold text-[#2563eb] uppercase tracking-wider">Sub-Tasks</span>
                <span className="text-xs text-white/40 ml-1">
                  {subSummaries[item.id].completed}/{subSummaries[item.id].total} done
                </span>
              </div>
              {subSummaries[item.id].completed === subSummaries[item.id].total && (
                <Badge variant="outline" className="text-2xs text-[#28C76F] border-[#28C76F]/30 py-0 h-4">All Complete</Badge>
              )}
              {subSummaries[item.id].overdue > 0 && (
                <Badge variant="outline" className="text-2xs text-red-400 border-red-400/30 py-0 h-4">{subSummaries[item.id].overdue} overdue</Badge>
              )}
            </div>
            <div className="space-y-1.5">
              {subSummaries[item.id].subs.map((sub) => (
                <div key={sub.id} className={`flex items-start gap-2 px-2.5 py-1.5 rounded text-sm border ${
                  sub.status === "COMPLETED"
                    ? "bg-[#28C76F]/10 border-[#28C76F]/20"
                    : "bg-white/5 border-white/10"
                }`} data-testid={`sub-task-row-${sub.id}`}>
                  <div className="pt-0.5 shrink-0">
                    {sub.status === "COMPLETED" ? <CheckCircle2 className="h-3 w-3 text-[#28C76F]" /> : <Users className="h-3 w-3 text-[#2563eb]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${sub.status === "COMPLETED" ? "text-[#28C76F]" : "text-white/70"}`}>{sub.assigned_to}</span>
                      {sub.due_date && (
                        <span className="text-xs text-white/30">
                          Due: {new Date(sub.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                      )}
                      <Badge variant="outline" className={`text-2xs py-0 h-4 ${
                        sub.status === "COMPLETED" ? "text-[#28C76F] border-[#28C76F]/30" : "text-white/40 border-white/10"
                      }`}>{sub.status === "COMPLETED" ? "Done" : "Pending"}</Badge>
                    </div>
                    {sub.instruction && (
                      <p className="text-white/40 text-xs mt-0.5 leading-relaxed truncate">{sub.instruction}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator className="bg-white/8 mb-3" />

        {!canDoAnything && (
          <div
            className="mb-2 px-2.5 py-1.5 rounded text-xs text-white/50 bg-white/3 border border-white/8"
            data-testid={`text-locked-reason-${item.id}`}
            title={lockedReason}
          >
            {lockedReason}
          </div>
        )}

        <div className="flex flex-col gap-2">
          {canActAsHolder && (
            <Textarea
              value={updateText}
              onChange={(e) =>
                setUpdateTexts((prev) => ({
                  ...prev,
                  [item.id]: e.target.value,
                }))
              }
              placeholder="Add progress update..."
              rows={2}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/25 text-sm resize-none"
              data-testid={`input-update-${item.id}`}
            />
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {canActAsHolder && getFilteredTransitions(item.status).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <ArrowRightLeft className="h-3.5 w-3.5 text-white/30" />
              <Select
                value=""
                onValueChange={(v) => handleStatusSelect(item.id, v, item.assigned_to)}
              >
                <SelectTrigger
                  className="w-44 bg-white/5 border-white/10 text-white text-xs h-8"
                  data-testid={`select-status-${item.id}`}
                >
                  <SelectValue placeholder="Transition to..." />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredTransitions(item.status).map((s) => (
                    <SelectItem key={s} value={s}>
                      {V2_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {statusTransition.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-white/50" />
              )}
            </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {item.status === "IN_PROGRESS" &&
                (sdfMdTarget || sdfPmoTarget) &&
                canActAsHolder && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isSubmitting}
                        className="gap-1.5 text-[#28C76F] border-[#28C76F]/30 hover:bg-[#28C76F]/10 hover:text-[#28C76F] h-8 text-xs"
                        data-testid={`button-send-for-review-${item.id}`}
                      >
                        {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Send for Review
                        <ChevronDown className="h-3 w-3 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {sdfMdTarget && (
                        <DropdownMenuItem
                          onClick={() => handleSendForReview(item.id, sdfMdTarget)}
                          data-testid={`menu-send-sdf-md-${item.id}`}
                        >
                          To SDF MD
                        </DropdownMenuItem>
                      )}
                      {sdfPmoTarget && (
                        <DropdownMenuItem
                          onClick={() => handleSendForReview(item.id, sdfPmoTarget)}
                          data-testid={`menu-send-sdf-pmo-${item.id}`}
                        >
                          To SDF PMO
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              {/* Strict authority model (v2.10.x):
                  - The OWNER on their own card sees the full "Hand to..."
                    dropdown — they can re-route the task they own to any
                    legitimate target. This is the only role allowed to
                    forward to a third party.
                  - A HOLDER who is NOT the owner sees only a single
                    "Return to owner" button. They cannot dump the task
                    onto a third party — that would let a non-owner
                    silently break the original delegation chain (Arun
                    delegates to Samer; Samer offloads to Zeyana without
                    Arun's consent). To re-route, they must return it to
                    the owner first.
                  - Anyone else: nothing. */}
              {canActAsHolder && !iAmOwner && item.assigned_to && (
                <>
                  <Forward className="h-3.5 w-3.5 text-white/30" />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isSubmitting}
                    onClick={() => {
                      // Clear any stale inline handoff state for this item
                      // and pass the owner directly as a synchronous override
                      // — see comment on handleSubmitUpdate for why.
                      setInlineHandoffDates(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                      setInlineHandoffReasons(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                      handleSubmitUpdate(item.id, item.assigned_to!);
                    }}
                    className="gap-1.5 text-[#FF9F43] border-[#FF9F43]/30 hover:bg-[#FF9F43]/10 hover:text-[#FF9F43] h-8 text-xs"
                    data-testid={`button-return-to-owner-${item.id}`}
                    title={`Return this task to ${item.assigned_to} (the owner). They are the only person who can re-route it from here.`}
                  >
                    {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Forward className="h-3.5 w-3.5" />}
                    Return to {item.assigned_to}
                  </Button>
                </>
              )}
              {canReassignFromOwnSection && (
                <>
                  <Forward className="h-3.5 w-3.5 text-white/30" />
                  <Select
                    value={inlineHandoffSelections[item.id] || ""}
                    onValueChange={(v) => setInlineHandoffSelections(prev => ({ ...prev, [item.id]: v }))}
                  >
                    <SelectTrigger
                      className="w-44 bg-white/5 border-white/10 text-white text-xs h-8"
                      data-testid={`select-handto-${item.id}`}
                    >
                      <SelectValue placeholder="Hand to..." />
                    </SelectTrigger>
                    <SelectContent>
                      {responsibilityTargets.map((t) => (
                        <SelectItem key={t.item_key} value={t.label}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              {canReassignFromOwnSection && inlineHandoffSelections[item.id] && (
                <>
                  <input
                    type="date"
                    value={inlineHandoffDates[item.id] || ""}
                    onChange={(e) => setInlineHandoffDates(prev => ({ ...prev, [item.id]: e.target.value }))}
                    className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 h-8 [color-scheme:dark]"
                    title="Due back by (optional internal deadline)"
                    aria-label="Due back by — optional internal deadline for this handoff"
                    data-testid={`input-inline-internal-due-date-${item.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setInlineHandoffSelections(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                      setInlineHandoffDates(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                      setInlineHandoffReasons(prev => { const n = { ...prev }; delete n[item.id]; return n; });
                    }}
                    className="text-white/40"
                    data-testid={`button-clear-handto-${item.id}`}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                  </Button>
                  {inlineHandoffDates[item.id] && (
                    <input
                      type="text"
                      value={inlineHandoffReasons[item.id] || ""}
                      onChange={(e) => setInlineHandoffReasons(prev => ({ ...prev, [item.id]: e.target.value }))}
                      placeholder="Why this internal date? (optional)"
                      maxLength={500}
                      className="basis-full bg-white/5 border border-white/10 text-white text-xs rounded px-2 h-8 placeholder:text-white/30"
                      aria-label="Why this internal date — optional context for the receiver"
                      data-testid={`input-inline-internal-due-reason-${item.id}`}
                    />
                  )}
                </>
              )}
              {/* Submit Update button — visible to:
                  - the holder (text-only progress update path), and
                  - the owner on their own card (reassign-with-reason path,
                    requires picking a Hand to target).
                  The Return-to-owner button above triggers handleSubmitUpdate
                  directly with the owner pre-selected, so it doesn't need
                  this button to be visible. */}
              {canDoAnything && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={
                    (!updateText.trim() && !inlineHandoffSelections[item.id]) ||
                    isSubmitting ||
                    // Owners who only have reassign-with-reason rights (not
                    // holding the task) must select a target — they cannot
                    // log a free-text progress update under the holder's name.
                    (!canActAsHolder && !inlineHandoffSelections[item.id])
                  }
                  onClick={() => handleSubmitUpdate(item.id)}
                  className="gap-1.5 text-[#00CFE8]"
                  data-testid={`button-submit-update-${item.id}`}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  {isSubmitting ? "Sending..." : inlineHandoffSelections[item.id] ? "Submit & Hand off" : "Submit Update"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const postponeModal = postponeModalState ? (
    <PostponeDialog
      state={postponeModalState}
      onClose={() => setPostponeModalState(null)}
      isSubmitting={postponeMutation.isPending}
      onSubmit={(payload) => postponeMutation.mutate({ itemId: postponeModalState.itemId, ...payload })}
    />
  ) : null;

  const reasonModal = reasonModalState ? (
    <HandoffReasonModal
      open={true}
      onOpenChange={(o) => { if (!o) setReasonModalState(null); }}
      currentHolder={reasonModalState.currentHolder}
      newHolder={reasonModalState.handTo}
      itemTitle={reasonModalState.title}
      isSubmitting={submittingIds.has(reasonModalState.itemId)}
      canFlagInsufficientProgress={canConfirmDroppedTheBall}
      cSuitePeople={cSuiteNames}
      onConfirm={handleConfirmReason}
    />
  ) : null;

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)" }}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#00CFE8]" />
          <span className="text-white/70 text-sm">Loading action items...</span>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)" }}
      >
        <Card className="border-red-500/30 bg-red-500/10 max-w-md">
          <CardContent className="p-6 flex flex-col items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-400" />
            <p className="text-white text-center">
              Failed to load action items. Please try again.
            </p>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              data-testid="button-retry"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0d1b3e 0%, #1a2a52 50%, #0f1d40 100%)" }}
    >
      {reasonModal}
      {postponeModal}
      <div className={`${containerClass} mx-auto px-6 py-8`}>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="h-10 w-10 rounded-md flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #00CFE8, #0098B3)" }}
            >
              <ClipboardCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-white" data-testid="text-page-title">
                Execution Tracker
              </h1>
              <p className="text-white/50 text-sm">
                What I'm holding · what I've delegated
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex items-center gap-3">
              <ClipboardCheck className="h-5 w-5 text-[#00CFE8]" />
              <div>
                <p className="text-2xl font-semibold text-white" data-testid="text-stat-open">
                  {openItems.length}
                </p>
                <p className="text-white/50 text-xs">Open Items</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div>
                <p className="text-2xl font-semibold text-red-400" data-testid="text-stat-overdue">
                  {totalOverdue}
                </p>
                <p className="text-white/50 text-xs">Overdue</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-4 flex items-center gap-3">
              <Forward className="h-5 w-5 text-[#FF9F43]" />
              <div>
                <p className="text-2xl font-semibold text-white" data-testid="text-stat-delegated">
                  {myDelegatedItems.length}
                </p>
                <p className="text-white/50 text-xs">Delegated by me</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {mySubs.length > 0 && (
          <Card className="border-[#2563eb]/30 mb-6" style={{ background: "rgba(37,99,235,0.08)" }} data-testid="card-my-sub-tasks">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <ListChecks className="h-5 w-5 text-[#2563eb]" />
                <h2 className="text-sm font-semibold text-[#2563eb]">
                  My Sub-Tasks ({mySubs.length} pending)
                </h2>
              </div>
              <div className="space-y-3">
                {mySubs.map((sub) => {
                  const subOverdue = isOverdue(sub.due_date);
                  const days = daysUntilDue(sub.due_date);
                  const note = subCompletionNotes[sub.id] ?? "";
                  return (
                    <div
                      key={sub.id}
                      className={`p-3 rounded-md border ${subOverdue ? "bg-red-500/8 border-red-400/30" : "bg-white/5 border-white/10"}`}
                      data-testid={`my-sub-task-${sub.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <a
                            href={`/action-items/${sub.action_item_id}`}
                            className="text-white font-medium text-sm hover:text-[#00CFE8] transition-colors"
                          >
                            {sub.action_item.title}
                          </a>
                          <div className="flex items-center gap-2 mt-0.5 text-xs">
                            <span className="text-white/40">{getLaneDisplay(sub.action_item.authority_lane)}</span>
                            <span className="text-white/30">From: {sub.assigned_by}</span>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-xs ${sub.action_item.priority === "P1" ? "text-red-400 border-red-400/30" : sub.action_item.priority === "P2" ? "text-orange-400 border-orange-400/30" : "text-white/50 border-white/20"}`}>
                          {sub.action_item.priority}
                        </Badge>
                      </div>
                      <div className="p-2.5 rounded bg-[#2563eb]/5 border border-[#2563eb]/15 mb-2">
                        <p className="text-xs font-semibold text-[#2563eb]/60 uppercase tracking-wider mb-1">Your Task</p>
                        <p className="text-white/70 text-xs leading-relaxed whitespace-pre-wrap">{sub.instruction}</p>
                      </div>
                      <div className="flex items-center gap-3 mb-3 text-xs">
                        <span className={`flex items-center gap-1 ${subOverdue ? "text-red-400 font-medium" : "text-white/50"}`}>
                          {subOverdue ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                          Due {formatDate(sub.due_date)}
                          {subOverdue
                            ? ` (${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue)`
                            : days === 0
                              ? " (today)"
                              : days <= 7
                                ? ` (${days} day${days !== 1 ? "s" : ""} left)`
                                : ""}
                        </span>
                      </div>
                      <Separator className="bg-white/8 mb-3" />
                      <div className="flex flex-col gap-2">
                        <Textarea
                          value={note}
                          onChange={(e) => setSubCompletionNotes(prev => ({ ...prev, [sub.id]: e.target.value }))}
                          placeholder="Optional completion note..."
                          rows={2}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/25 text-sm resize-none"
                          data-testid={`input-sub-complete-note-${sub.id}`}
                        />
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            disabled={completeSubMutation.isPending}
                            onClick={() => {
                              completeSubMutation.mutate({ subId: sub.id, note: note || undefined });
                            }}
                            className="gap-1.5 text-xs bg-[#28C76F] hover:bg-[#28C76F]/80"
                            data-testid={`button-complete-sub-${sub.id}`}
                          >
                            {completeSubMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            Complete & Hand Back
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {openItems.length === 0 ? (
          <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-8 flex flex-col items-center gap-3">
              <ClipboardCheck className="h-10 w-10 text-[#28C76F]" />
              <p className="text-white/70" data-testid="text-empty-state">
                No open action items. All caught up.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Section 1 — My Action Items (full interactive). Items I am
                currently holding, whether I own them or someone delegated
                them to me. This is where the work gets done. */}
            <Card
              className="border-white/10"
              style={{ background: "rgba(255,255,255,0.06)" }}
              data-testid="card-my-action-items"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium text-white flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-[#00CFE8]" />
                    <span>My Action Items</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[#00CFE8] border-[#00CFE8]/30">
                      {myHeldItems.length} {myHeldItems.length === 1 ? "item" : "items"}
                    </Badge>
                    {myHeldItems.filter(i => isOverdue(i.due_date)).length > 0 && (
                      <Badge variant="outline" className="text-red-400 border-red-400/30">
                        {myHeldItems.filter(i => isOverdue(i.due_date)).length} overdue
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 pt-0">
                {myHeldItems.length === 0 ? (
                  <p className="text-white/40 text-xs italic py-3" data-testid="text-empty-my-items">
                    Nothing on your plate right now.
                  </p>
                ) : (
                  myHeldItems.map((item) => renderItemRow(item, item.assigned_to !== teamMemberName, true))
                )}
              </CardContent>
            </Card>

            {/* Section 2 — Tasks I've Delegated (compact, read-only +
                Reassign/Postpone). Items I OWN but someone else holds.
                Sorted by due date, overdue first. */}
            {myDelegatedItems.length > 0 && (
              <Card
                className="border-white/10"
                style={{ background: "rgba(255,255,255,0.06)" }}
                data-testid="card-my-delegated"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium text-white flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Forward className="h-4 w-4 text-[#FF9F43]" />
                      <span>Tasks I've Delegated</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[#FF9F43] border-[#FF9F43]/30">
                        {myDelegatedItems.length} out
                      </Badge>
                      {myDelegatedItems.filter(i => isOverdue(i.due_date)).length > 0 && (
                        <Badge variant="outline" className="text-red-400 border-red-400/30">
                          {myDelegatedItems.filter(i => isOverdue(i.due_date)).length} overdue
                        </Badge>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 pt-0">
                  {myDelegatedItems.map(renderDelegatedRow)}
                </CardContent>
              </Card>
            )}

            {/* Admin / dept-head fallback — items neither held nor owned
                by the viewer that were nonetheless returned by the
                backend (dept-head widening). Hidden by default to keep
                the page personal; if visibility is needed, use the KPI
                Dashboard or Origins page instead. */}
            {otherItems.length > 0 && (
              <Card
                className="border-white/10"
                style={{ background: "rgba(255,255,255,0.04)" }}
                data-testid="card-other-items"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-white/60 flex items-center gap-2">
                    <Users className="h-4 w-4 text-white/40" />
                    <span>Other open items in your scope</span>
                    <Badge variant="outline" className="text-white/50 border-white/15">
                      {otherItems.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-white/40 text-xs italic">
                    These tasks are in your visibility scope but you neither hold nor own them. Open the KPI Dashboard for a full team view.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Dialog open={!!pendingTransition} onOpenChange={(open) => { if (!open) setPendingTransition(null); }}>
        <DialogContent className="bg-[#1a1f2e] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Transition</DialogTitle>
          </DialogHeader>
          <p className="text-white/70 text-sm">
            Are you sure you want to move this item to{" "}
            <span className="font-semibold text-[#FF9F43]">
              {pendingTransition ? V2_STATUS_LABELS[pendingTransition.status as V2ActionItemStatus] || pendingTransition.status : ""}
            </span>?
            {pendingTransition?.status === "CANCELLED" && (
              <span className="block mt-1 text-red-400 text-xs">This action cannot be undone.</span>
            )}
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setPendingTransition(null)} className="border-white/10 text-white/60" data-testid="button-cancel-transition">Cancel</Button>
            <Button
              size="sm"
              disabled={statusTransition.isPending}
              onClick={() => {
                if (pendingTransition) {
                  statusTransition.mutate(pendingTransition, {
                    onSettled: () => setPendingTransition(null),
                  });
                }
              }}
              className="bg-[#FF9F43] hover:bg-[#FF9F43]/80 text-black"
              data-testid="button-confirm-transition"
            >
              {statusTransition.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
